import React from "react";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import _, { isArray } from "lodash";
import { Tag } from "antd";
import { LeftOutlined, RightOutlined, NodeIndexOutlined, FallOutlined, RiseOutlined, DashboardOutlined, ThunderboltOutlined, HeartOutlined, CompassOutlined, StarOutlined, ClockCircleOutlined, LoginOutlined, FireOutlined, RetweetOutlined, CloudOutlined, WifiOutlined, ApiOutlined } from "@ant-design/icons";
import { DashboardFilled, ThunderboltFilled, HeartFilled, CompassFilled, StarFilled, ClockCircleFilled, FireFilled, InteractionFilled, CloudFilled } from "@ant-design/icons";
import { FILE_TYPE, ACTION_TYPE, STATS_TYPE, DATE_TIME_FORMAT, SPORT_TYPE, SPORT_ICONS } from "constants/index";

import GeneralService from "../services/general.service";

function isNumeric(str) {
  if (typeof str != "string") return false // we only process strings!  
  return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
         !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
}

const buildDisplayName = (user) => {
  if (user.firstName === "" && user.lastName === "") {
    return user.userName;
  }
  return `${user.firstName} ${user.lastName}`;
};

const formatFlagged = (string, multiTags = false) => {
  if (string === null || string === undefined || string === "undefined")
    return "";

  let flagged = string.split(",");
  if (multiTags) {
    return flagged.map(s => <Tag color="red">{s}</Tag>);
  }
  else {
    return <Tag color="red">{flagged.join(", ")}</Tag>;
  }
};

const formatDisplayNumber = (string, fractionDigits = 1, stats = null, includedPrefix = false, includedUnit = false, highlight = false, hasTag = true) => {
  if (string === null || string === undefined || string === "undefined")
    return "";

  if (Array.isArray(string) && string.join() === ",") {
    return "";
  }
  
  const value = checkArrayInput(string, fractionDigits, stats, includedPrefix, includedUnit, highlight);

  if (stats === STATS_TYPE.Heartrate) {
    if (hasTag) {
      return (
        <React.Fragment>
            <Tag>{includedPrefix ? highlight ? <HeartFilled style={{color: "#ff4d4f"}} /> : <HeartOutlined /> : "HR "}{value}</Tag>
        </React.Fragment>
      );
    }
    else {
      return (
        <React.Fragment>
            {includedPrefix ? highlight ? <HeartFilled style={{color: "#ff4d4f"}} /> : <HeartOutlined /> : "HR "}{value}
        </React.Fragment>
      );
    }
  }
  else if (stats === STATS_TYPE.Cadence) {
    if (hasTag) {
      return (
        <React.Fragment>
            <Tag>{includedPrefix ? <CompassFilled /> : ""}{value}</Tag>
        </React.Fragment>
      );
    }
    else {
      return (
        <React.Fragment>
            {includedPrefix ? <CompassFilled /> : ""}{value}
        </React.Fragment>
      );
    }
  }
  else if (stats === STATS_TYPE.Power) {
    if (hasTag) {
      return (
        <React.Fragment>
            <Tag>{includedPrefix ? highlight ? <ThunderboltFilled style={{color: "#ffa940"}} /> : <ThunderboltOutlined /> : ""}{value}</Tag>
        </React.Fragment>
      );
    }
    else {
      return (
        <React.Fragment>
            {includedPrefix ? highlight ? <ThunderboltFilled style={{color: "#ffa940"}} /> : <ThunderboltOutlined /> : ""}{value}
        </React.Fragment>
      );
    }
  }
  else if (stats === STATS_TYPE.Calories) {
    if (hasTag) {
      return (
        <React.Fragment>
            <Tag>{includedPrefix ? highlight ? <FireFilled style={{color: "#ff7a45"}} /> : <FireOutlined /> : ""}{value}</Tag>
        </React.Fragment>
      );
    }
    else {
      return (
        <React.Fragment>
            {includedPrefix ? highlight ? <FireFilled style={{color: "#ff7a45"}} /> : <FireOutlined /> : ""}{value}
        </React.Fragment>
      );
    }
  }
  else if (stats === STATS_TYPE.Temp) {
    if (hasTag) {
      return (
        <React.Fragment>
            <Tag>{includedPrefix ? highlight ? <CloudFilled style={{color: "#000"}} /> : <CloudOutlined /> : ""}{value}</Tag>
        </React.Fragment>
      );
    }
    else {
      return (
        <React.Fragment>
            {includedPrefix ? highlight ? <CloudFilled style={{color: "#000"}} /> : <CloudOutlined /> : ""}{value}
        </React.Fragment>
      );
    }
  }
  return value;
};

const checkArrayInput = (str, fractionDigits = 1, stats = null, includedPrefix = false, includedUnit = false, highlight = false) => {
  if (!Array.isArray(str)) {
    return Number.parseFloat(str).toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }) + (stats && includedUnit ? ` ${stats}` : "");
  }
  else {
    str = str.filter(s => s !== null);
    if (str.length < 2) {
      return str + (stats && includedUnit ? ` ${stats}` : "");
    }
    return <label>{str[0]} [<span style={{color: '#a335ee'}}>{str[1]}</span>] {(stats && includedUnit ? ` ${stats}` : "")}</label>;
  }
}

const formatCadence = (string, fractionDigits = 1, sport = SPORT_TYPE.Run, includedPrefix = false, includedUnit = false, highlight = false) => {
  if (string === null || string === undefined || string === "undefined")
    return "";

  if (sport === SPORT_TYPE.Ride) {
    const value = Number.parseFloat(string).toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }) + (includedUnit ? ` ${STATS_TYPE.RideCadence}` : "");

    return (
      <React.Fragment>
          <Tag>{includedPrefix ? <DashboardOutlined style={{color: highlight ? "#000" : "#000"}} /> : ""}{value}</Tag>
      </React.Fragment>
    );
  }
  else if (sport === SPORT_TYPE.Run || sport === SPORT_TYPE.Hike || sport === SPORT_TYPE.Walk) {
    const value = (Number.parseFloat(string) * 2).toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }) + (includedUnit ? ` ${STATS_TYPE.Cadence}` : "");

    return (
      <React.Fragment>
          <Tag>{includedPrefix ? <DashboardOutlined style={{color: highlight ? "#000" : "#000"}} /> : ""}{value}</Tag>
      </React.Fragment>
    );
  }
  else {
    const value = Number.parseFloat(string).toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }) + (includedUnit ? ` ${STATS_TYPE.Cadence}` : "");

    return (
      <React.Fragment>
          <Tag>{includedPrefix ? <DashboardOutlined style={{color: highlight ? "#000" : "#000"}} /> : ""}{value}</Tag>
      </React.Fragment>
    );
  }
};

const formatDistance = (metters, fractionDigits = 1, includedUnit = false) => {
  if (metters === null || metters === undefined || metters === "undefined")
    return "N/A";
  return Number.parseFloat(metters/1000).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }) + (includedUnit ? " km" : "");
};

const formatDisplayTime = (seconds, hourOnly = false, includedUnit = false) => {
  if (seconds === null || seconds === undefined || seconds === "undefined")
    return "N/A";
  let secs = seconds;
  let mins = Math.floor(secs / 60);
  secs = Math.round(secs % 60);
  let hours = Math.floor(mins/60)
  mins = mins%60;

  if (hourOnly) {
    return `${mins >= 30 ? hours + 1 : hours}` + (includedUnit ? " hr" : "");;
  }
  else {
    // adjust rounding
    if (secs === 60) {
        mins += 1;
        secs = 0;
    }
    if (mins === 60) {
        hours += 1;
        mins = 0;
    }

    return `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
};

const calculateTotal = (activities, search, key = "distance") => {
  let data = activities;
  if (search) data = activities.filter((s) => s[search.field] === search.value);
  if (data && data.length > 0) {
    if (data.length === 1) {
      return formatDistance(data[0][key], 2, true);
    }
    else {
      return formatDistance(data.reduce((sum, record) => sum + (record[key] || 0), 0), 2, true);
    }
  }
  else {
    return formatDistance(0, 2, true);
  }
};

const calculateTotalDistance = (activities) => {
  let data = activities;
  if (data && data.length > 0) {
      return formatDistance(data.reduce((sum, record) => sum + (record.distance || 0), 0), 1, true);
  } 
  else {
    return formatDistance(0, 2, true);
  }
};

const calculateTotalGain = (activities) => {
  let data = activities;
  if (data && data.length > 0) {
      return formatDisplayNumber(data.reduce((sum, record) => sum + (record.total_elevation_gain || 0), 0), 0, STATS_TYPE.Elevation, false, true, false);
  } 
  else {
    return formatDisplayNumber(0, 0, STATS_TYPE.Elevation, false, true, false)
  }
};

const calculatePace = (objSpeed, sport = SPORT_TYPE.Run, includedPrefix = false, includedUnit = false, highlight = false) => {
  if (!Array.isArray(objSpeed)) {
    return "N/A";
  }

  let arrPace = [];
  if (sport === SPORT_TYPE.Ride) {
    for (let speed of objSpeed) {
      if (speed.metters === null || speed.metters === undefined || speed.metters === "undefined" || speed.metters === 0 || speed.seconds === null || speed.seconds === undefined || speed.seconds === "undefined" || speed.seconds === 0) {
        arrPace.push("N/A");
        continue;
      }
      arrPace.push(((speed.metters/1000)/(speed.seconds/60/60)).toFixed(1));
    }

    return (
      <React.Fragment>
          <Tag>{includedPrefix ? <img src={SPORT_ICONS[sport]} /> : ""}{checkArrayInput(arrPace)}{includedUnit ? " km/h" : ""}</Tag>
      </React.Fragment>
    );
  }
  else if (sport === SPORT_TYPE.Swim) {
    for (let speed of objSpeed) {
      if (speed.metters === null || speed.metters === undefined || speed.metters === "undefined" || speed.metters === 0 || speed.seconds === null || speed.seconds === undefined || speed.seconds === "undefined" || speed.seconds === 0) {
        arrPace.push("N/A");
        continue;
      }
      let secs = speed.seconds/(speed.metters/100);
      let mins = Math.floor(secs / 60);
      secs = Math.round(secs % 60);

      // adjust pace rounding
      if (secs === 60) {
        mins += 1;
        secs = 0;
      }

      arrPace.push(`${mins}:${secs.toString().padStart(2, "0")}`);
    }
    
    return (
      <React.Fragment>
          <Tag>{includedPrefix ? <img src={SPORT_ICONS[sport]} /> : ""}{checkArrayInput(arrPace)}{includedUnit ? " /100m" : ""}</Tag>
      </React.Fragment>
    );
  }
  else {
    for (let speed of objSpeed) {
      if (speed.metters === null || speed.metters === undefined || speed.metters === "undefined" || speed.metters === 0 || speed.seconds === null || speed.seconds === undefined || speed.seconds === "undefined" || speed.seconds === 0) {
        arrPace.push("N/A");
        continue;
      }
      let secs = speed.seconds/(speed.metters/1000);
      let mins = Math.floor(secs / 60);
      secs = Math.round(secs % 60);

      // adjust pace rounding
      if (secs === 60) {
        mins += 1;
        secs = 0;
      }

      arrPace.push(`${mins}:${secs.toString().padStart(2, "0")}`);
    }

    return (
      <React.Fragment>
          <Tag>{includedPrefix ? <img src={SPORT_ICONS[sport]} /> : ""}{checkArrayInput(arrPace)}{includedUnit ? " /km" : ""}</Tag>
      </React.Fragment>
    );
  }
};

const calculateIncline = (elevation, metters, includedPrefix = false, includedUnit = false, highlight = false) => {
  if (elevation === null || elevation === undefined || elevation === "undefined" || elevation === 0) {
    return "";
  }
  if (metters === null || metters === undefined || metters === "undefined" || metters === 0) {
    return "";
  }

  let incline = Math.round((elevation/metters) * 100); // round up
  //let incline = ((elevation/metters) * 100).toFixed(1); // 0.1 decimal accuracy
  if (incline <= 0) {
    return "";
  }
  
  return <Tag className="additional-stats">{includedPrefix ? <RiseOutlined style={{color: highlight ? "#f50" : "#000"}} /> : ""} {incline}{includedUnit ? " %" : ""}</Tag>;
};

const calculateImprovement = (present, past) => {
  if (present === null || present === undefined || present === "undefined" || present === 0) {
    return "";
  }
  if (past === null || past === undefined || past === "undefined" || past === 0) {
    return "";
  }

  let improvement = Math.round((present-past) / past * 100);
  if (improvement <= -2000 || improvement >= 2000) {
    return "";
  }

  return <span className="improvement">{improvement < 0 ? <FallOutlined style={{color: "#f50"}} /> : <RiseOutlined style={{color: "#f50"}} />} {Math.abs(improvement)} %</span>;
};

const markVirtualSport = (sport_type, highlight = false) => {
  if (!sport_type || sport_type.indexOf("Virtual") < 0) {
    return "";
  }
  
  return <Tag className="additional-stats"><ApiOutlined style={{color: highlight ? "#f50" : "#000"}} /></Tag>;
};

const exportFile = (tableData, columnData, fileType, fileName) => {
  let csvData = ``;
  let xlsData = [];
  let header = `<thead><tr>`;
  for (var col of columnData.labels) {
    switch (col) {
      case "Deliveries":
        header += tableData[0]["deliveries"].map((s, idx) => {
          return `<td>${idx + 1}</td>`;
        });
        csvData += tableData[0]["deliveries"].map((s, idx) => {
          return `,${idx + 1}`;
        });
        break;
      default:
        header += `<th>${col}</th>`;
        csvData += `,${col}`;
    }
  }
  header += `</tr></thead>`;
  csvData += `\r\n`;

  let content = `<tbody>`;
  for (var row of tableData) {
    content += `<tr>`;
    let rowResult = {};
    let index = 0;
    for (var col of columnData.fields) {
      //PDF & CSV//
      switch (col) {
        case "deliveries":
          content += row[col].map((s) => {
            return `<td>${s}</td>`;
          });
          csvData += row[col].map((s) => {
            return `,${s}`;
          });
          break;
        case "tasks":
          content += `<td>${row[col].map((s) => {
            return `${s.task.don} (${formatDisplayNumber(
              s.quantity,
              0
            )} kg)<br/>`;
          })}</td>`;
          csvData += `,"${row[col].map((s) => {
            return `${s.task.don} (${formatDisplayNumber(
              s.quantity,
              0
            )} kg)\r\n`;
          })}"`;
          break;
        default:
          content += `<td>${row[col]}</td>`;
          csvData += `,"${row[col]}"`;
      }
      //XLS//
      if (col.indexOf(".") >= 0) {
        const childs = col.split(".");
        rowResult[columnData.labels[index]] = row[childs[0]][childs[1]];
      } else if (
        col === "startdate" ||
        col === "enddate" ||
        col === "updateddate" ||
        col === "transportationdate" ||
        col === "lasttrxdate" ||
        col === "date"
      ) {
        rowResult[columnData.labels[index]] = moment(row[col]).format(
          DATE_TIME_FORMAT.DATE
        );
      } else if (col === "deliveries") {
        // for (let i = 0; i < row[col].length; i++) {
        //   const _col = i + 1;
        //   rowResult[" " + _col] = row[col][i];
        // }
        // rowResult[columnData.labels[index]] = row[col][0];
        row[col].map((s, idx) => {
          const _col = idx + 1;
          rowResult[" " + _col] = s;
        });
      } else if (col === "tasks") {
        let rowVal = "";
        row[col].map((s) => {
          rowVal += `${s.task.don} (${formatDisplayNumber(
            s.quantity,
            0
          )} kg)\r\n`;
        });
        rowResult[columnData.labels[index]] = rowVal;
      } else {
        rowResult[columnData.labels[index]] = row[col];
      }
      index++;
    }
    xlsData.push(rowResult);
    content += `</tr>`;
    csvData += `\r\n`;
  }
  content += `</tbody>`;

  if (fileType === FILE_TYPE.pdf) {
    const fileExtension = ".pdf";
    const htmlData = `<h3>${fileName}</h3><table class="exportFileContent">${header}${content}</table>`;
    // let iframe = document.createElement('iframe');
    let iframe = document.createElement("div");
    document.body.appendChild(iframe);
    setTimeout(function () {
      // let iframedoc = iframe.contentDocument || iframe.contentWindow.document;
      // iframedoc.body.innerHTML = htmlData;
      let iframedoc = iframe;
      iframedoc.innerHTML = htmlData;
      const width = 240 + columnData.length * 80 + 20 * 3.78;
      const height = (width * 70.7) / 100;
      html2canvas(iframedoc, {
        allowTaint: true,
        useCORS: true,
        logging: false,
        width: width,
        windowWidth: width,
      }).then((canvas) => {
        let pdf = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: [width / 3.78, height / 3.78],
        });
        let imgData = canvas.toDataURL("image/png", 1.0);
        pdf.addImage(imgData, "PNG", 10, 10);
        pdf.save(fileName + fileExtension);
        document.body.removeChild(iframe);
      });
    }, 10);
  }
  if (fileType === FILE_TYPE.xls) {
    const fileExtension = ".xlsx";
    const ws = XLSX.utils.json_to_sheet(xlsData);
    const wb = { Sheets: { Data: ws }, SheetNames: ["Data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, fileName + fileExtension);
  }
  if (fileType === FILE_TYPE.csv) {
    const fileExtension = ".csv";
    const data = new Blob([csvData], { type: fileType });
    FileSaver.saveAs(data, fileName + fileExtension);
  }
};

const GeneralHelper = {
  buildDisplayName,
  exportFile,
  formatFlagged,
  formatDistance,
  formatDisplayNumber,
  formatDisplayTime,
  formatCadence,
  calculatePace,
  calculateIncline,
  calculateTotal,
  calculateTotalDistance,
  calculateTotalGain,
  calculateImprovement,
  markVirtualSport
};

export default GeneralHelper;
